/** English strings.
 *
 * Keys missing here fall back to Russian — a visible fallback beats an
 * untranslated key like `game.title` staring at the player. The test suite
 * keeps both dictionaries in step: same keys, same placeholders.
 */

export const en: Record<string, string> = {
  // --- Common --------------------------------------------------------------
  'app.title': 'Sudokito',
  'app.loading': 'Preparing a puzzle…',
  'app.back': 'Back',
  'app.cancel': 'Cancel',
  'app.error.login': 'Could not sign in',
  'app.error.start': 'Could not start the game',
  'app.error.move': 'Could not make the move',
  'app.error.generic': 'Something went wrong',

  'plural.days': 'day|days|days',
  'plural.players': 'player|players|players',
  'plural.times': 'time|times|times',
  'plural.awards': 'award|awards|awards',
  'plural.places': 'place|places|places',
  'plural.minutes': 'minute|minutes|minutes',

  // --- Levels --------------------------------------------------------------
  'level.easy': 'Easy',
  'level.easy.hint': 'A warm-up',
  'level.medium': 'Medium',
  'level.medium.hint': 'An unhurried game',
  'level.hard': 'Hard',
  'level.hard.hint': 'Takes some thinking',
  'level.expert': 'Expert',
  'level.expert.hint': 'Advanced techniques',
  'level.master': 'Master',
  'level.master.hint': 'For the stubborn only',

  // --- Home ----------------------------------------------------------------
  'home.streak': 'Streak: {days} {word}',
  'home.noStreak': 'No streak yet',
  'home.continue': 'Continue the game',
  'home.continueMeta': '{level} · {time} · {filled} of 81 cells',
  'home.newGame': 'New game',
  'home.newGameHint': 'Choose a level',
  'home.more': 'More',
  'home.noGame': 'No unfinished games',
  'home.noGameHint':
    'Start with a lower level — progress is saved on its own, even if you close the messenger.',
  'home.best': 'best',
  'home.noRecord': 'no record',
  'home.rating': 'Rating and awards',
  'home.ratingHint': 'League, season, achievements',
  'home.shop': 'Shop',
  'home.shopHint': 'Themes and frames',
  'home.settings': 'Settings',
  'home.daily': 'Daily puzzle · {date}',
  'home.dailyTitle': 'One puzzle for everyone',
  'home.dailyMeta': '{level} · not solved yet',
  'home.dailySolvers': '{level} · {count} people solving it',
  'home.dailySolved': 'Solved in {time}',
  'home.dailyBetter': 'Better than {percent}% of players · come back tomorrow',
  'home.dailyDone': 'Solved · come back tomorrow',
  'home.play': 'Play',
  'home.dailyPlay': 'Take the challenge',

  // --- Mode picker ---------------------------------------------------------
  'mode.title': '{level} · how do we play?',
  'mode.calm': 'Relaxed',
  'mode.calmHint': 'Mistakes are not limited',
  'mode.hardcore': 'Hardcore',
  'mode.hardcoreHint': 'Three mistakes end the game',
  'mode.bonus': '{value} points',
  'mode.start': 'Start',

  // --- Game ----------------------------------------------------------------
  'game.title': 'Game',
  'game.undo': 'Undo',
  'game.redo': 'Redo',
  'game.erase': 'Erase',
  'game.notes': 'Notes',
  'game.hint': 'Hint',
  'game.notesHint': 'notes mode — the digit goes in as pencil',
  'game.offline': 'No connection — moves are saved and will be sent',
  'game.mistakes': '✕ {used}/{limit}',
  'game.hintsUsed': '{count} hints',
  'game.pause': 'Paused',
  'game.pauseHint': 'The board is hidden, the timer stopped at {time}. Filled {filled} of 81.',
  'game.resume': 'Continue',
  'game.pinned': 'Pinned {digit}',
  'game.abandon': 'Abandon the game',

  // --- Hint sheet ----------------------------------------------------------
  'hints.title': 'Hints',
  'hints.check': 'Check for mistakes',
  'hints.checkHint': 'Marks in red every cell whose digit is already wrong',
  'hints.technique': 'Suggest a move',
  'hints.techniqueHint': 'Opens a cell and explains why that digit belongs there',
  'hints.reveal': 'Reveal a cell',
  'hints.revealHint': 'Shows the digit in the cell you picked',
  'hints.revealNeedsCell': 'Pick a cell on the board first',
  'hints.autonotes': 'Fill in the notes',
  'hints.autonotesHint': 'Places candidates in every empty cell',
  'hints.free': 'Free',
  'hints.freeIn': 'A free one opens in {hours} h',
  'hints.notEnough': '{amount} ◆ short',
  'hints.notEnoughDaily': '{amount} ◆ short — the daily puzzle gives up to {reward}',
  'hints.earn': 'How to earn crystals',

  // --- Technique plaque ----------------------------------------------------
  'technique.tag': 'technique',
  'technique.opened': 'Cell {cell} opened · ◆ {cost} charged',
  'technique.openedFree': 'Cell {cell} opened · free hint of the day',
  'technique.paid': '◆ {cost} charged',
  'technique.got': 'Got it, playing on',

  // --- Win -----------------------------------------------------------------
  'result.solved': 'Puzzle solved',
  'result.levelSolved': '{level} solved',
  'result.dailySolved': 'Daily puzzle solved',
  'result.points': 'points',
  'result.time': 'Time',
  'result.mistakes': 'Mistakes',
  'result.hints': 'Hints',
  'result.speed': 'Speed multiplier',
  'result.accuracy': 'Accuracy multiplier',
  'result.mode': 'Hardcore multiplier',
  'result.coins': 'Crystals',
  'result.noHintBonus': 'Bonus for a game without hints',
  'result.record': 'Personal best on “{level}”',
  'result.recordFrom': 'was {old} — now {new}',
  'result.recordFirst': 'first result on this level',
  'result.streak': 'Streak: {days} {word} in a row',
  'result.achievement': 'New achievement: “{title}”',
  'result.again': 'One more game',
  'result.toLevels': 'Back to levels',
  'result.share': 'Share',
  'result.shareText': '{name} solved “{level}” in {time}. Care to try the same puzzle?',
  'result.inviteText': 'I am playing sudoku — join me',

  // --- Loss ----------------------------------------------------------------
  'loss.title': 'Out of attempts',
  'loss.hint':
    'Hardcore mode gives three. The board is saved — you can restart this very puzzle or take a calmer level. Your daily streak is intact.',
  'loss.filled': 'Filled',
  'loss.filledValue': '{filled} of 81',
  'loss.progressCoins': 'Crystals for progress',
  'loss.restart': 'Restart this puzzle',

  // --- Leaderboard ---------------------------------------------------------
  'rating.title': 'Rating and awards',
  'rating.tab.board': 'Rating',
  'rating.tab.stats': 'Stats',
  'rating.tab.awards': 'Awards',
  'rating.scope.total': 'All time',
  'rating.scope.weekly': 'This week',
  'rating.scope.daily': 'Daily puzzle',
  'rating.scope.best_time': 'Time by level',
  'rating.scope.friends': 'Friends',
  'rating.you': '{name} · you',
  'rating.delta': '+{count} {word} this week',
  'rating.deltaDown': '−{count} {word} this week',
  'rating.milestonePoints': '{gap} points to the top {rank}',
  'rating.milestoneTime': '{gap} s to the top {rank}',
  'rating.refresh': 'Updated every {minutes} {word}',
  'rating.bestTimeHeader': 'Best time · “{level}” · {count} {word}',
  'rating.timeNote': 'In the time table the value is larger than the name: that is what gets compared.',
  'rating.empty': 'Nothing here yet',
  'rating.emptyHint': 'Solve a game and you will show up in the table.',
  'rating.emptyDaily': 'Nobody has solved the daily puzzle yet',
  'rating.emptyDailyHint':
    'Be the first — you will take the top row and get double crystals.',
  'rating.openDaily': 'Open the daily puzzle',

  // --- Stats ---------------------------------------------------------------
  'stats.games': 'games played',
  'stats.wins': 'wins · {percent}%',
  'stats.streak': 'days in a row now',
  'stats.bestStreak': 'longest streak',
  'stats.place': 'Place in the overall standings',
  'stats.ofPlayers': 'of {count} {word}',
  'stats.noPlace': 'Not in the standings yet',
  'stats.bestTimes': 'Best time by level',
  'stats.notSolved': 'not solved yet',

  // --- Awards --------------------------------------------------------------
  'awards.progress': 'Earned {done} of {total}',
  'awards.more': '{count} more {word} below',

  // --- Shop ----------------------------------------------------------------
  'shop.title': 'Shop',
  'shop.note': 'A skin changes how the board looks — that is what you buy.',
  'shop.chosen': 'Chosen',
  'shop.choose': 'Choose',
  'shop.bought': 'Owned · Choose',
  'shop.notEnough': '{amount} ◆ short',
  'shop.locked': 'Opens on day {days} of a streak',
  'shop.lockedProgress': '{current} of {total} days',
  'shop.frames': 'Avatar frames',
  'shop.framesNote': 'Other players see your frame in the leaderboard.',

  // --- Crystals ------------------------------------------------------------
  'crystals.title': 'Crystals',
  'crystals.balance': 'in your balance',
  'crystals.howTo': 'How to earn',
  'crystals.daily': 'Daily puzzle',
  'crystals.dailyHint': 'Once a day, twice as much without mistakes',
  'crystals.dailyValue': 'up to ◆ {max}',
  'crystals.streak': 'Daily streak',
  'crystals.streakHint': 'Every day in a row · {days} {word} now',
  'crystals.streakValue': '◆ {amount} / day',
  'crystals.win': 'Winning a game',
  'crystals.winHint': 'The higher the level, the more',
  'crystals.awards': 'Achievements',
  'crystals.awardsHint': '{count} of {total} left to earn',
  'crystals.noHints': 'A game without hints',
  'crystals.noHintsHint': 'A bonus on top of the win reward',
  'crystals.range': '◆ {min}–{max}',
  'crystals.plus': '+ ◆ {amount}',
  'crystals.spend': 'Where they go',
  'crystals.spendHint':
    'Check for mistakes ◆ {check} · suggest a move ◆ {technique} · reveal a cell ◆ {reveal} · notes ◆ {autonotes}',
  'crystals.openDaily': 'Open the daily puzzle',

  // --- Settings ------------------------------------------------------------
  'settings.title': 'Settings',
  'settings.digitFirst': 'Digit first, then cells',
  'settings.digitFirstHint': 'Place one digit across many cells',
  'settings.autoClear': 'Auto-clear notes',
  'settings.autoClearHint': 'Remove candidates after a move',
  'settings.haptics': 'Haptic feedback',
  'settings.hapticsHint': 'Entry, mistake, win',
  'settings.sound': 'Sound',
  'settings.soundHint': 'Short tones on entry and on winning',
  'settings.notifications': 'Bot notifications',
  'settings.notificationsHint': 'The daily puzzle and a streak about to break',
  'settings.language': 'Language',
  'settings.techniques': 'Techniques',
  'settings.techniquesHint': 'Learned {done} of {total}',
  'settings.invite': 'Invite a friend',
  'settings.inviteHint': 'The reward comes after your friend’s first win',

  // --- Techniques ----------------------------------------------------------
  'techniques.title': 'Techniques',
  'techniques.progress': 'Learned {done} of {total}',
  'techniques.source': 'collected from hints',
  'techniques.used': 'used {count} {word}',
  'techniques.locked': 'Not met yet',
  'techniques.willOpen': 'opens with a hint',
  'techniques.forever': 'Every technique opens once and stays forever.',

  // --- Dialogs -------------------------------------------------------------
  'dialog.activeGame.title': 'A game is already running',
  'dialog.activeGame.body':
    '{level}, {time}, {filled} of 81 filled. Starting a new one loses that progress.',
  'dialog.activeGame.continue': 'Continue the current one',
  'dialog.activeGame.new': 'Start a new one',
  'dialog.funds.title': 'Not enough crystals',
  'dialog.funds.need': 'Need',
  'dialog.funds.have': 'have',
  'dialog.funds.hint': 'The daily puzzle gives up to ◆ {daily}, a streak — ◆ {streak} a day.',
  'dialog.funds.earn': 'How to earn',
  'dialog.funds.later': 'Later',
  'dialog.puzzlePlayed': 'You have already played this puzzle — comparing times would not be fair.',
}
